import { Cell } from '@pelckmans/elementis/components/table';
import DetailsIcon from '@pelckmans/elementis/icons/details';
import RetryIcon from '@pelckmans/elementis/icons/retry';
import { useContext, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { getMedialinkById } from '../../../../../../api/medialink';
import { getUserMaterial } from '../../../../../../api/user-material';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../../../constants/analyticsEvents';
import ModalContext from '../../../../../../contexts/ModalContext';
import useAnalytics from '../../../../../../hooks/useAnalytics';
import ShareModal from '../../../../../../shared-components/share-modal/ShareModal';
import HorizontalScrollContext from '../../../../../../shared-components/table/context/HorizontalScrollContext';
import ModuleContext from '../../../../_contexts/ModuleContext';
import { ScorableItem } from '../../../api/scorable-items';
import { useFilters } from '../../../context/filter-context/FilterContext';
import HistoryModal from '../../history-modal/HistoryModal';

export default function ActionCell({
  scorableItem,
  memberId,
  groupId,
  isAssignment,
  amountOfSessions,
}: {
  scorableItem: ScorableItem;
  memberId: string;
  groupId: string;
  isAssignment: boolean;
  amountOfSessions: number;
}) {
  const { t } = useTranslation();
  const { showModal } = useContext(ModalContext);
  const modalId = useId();
  const { selectedModule } = useFilters();
  const { module } = useContext(ModuleContext);
  const { capture } = useAnalytics();
  const { horizontalScroll } = useContext(HorizontalScrollContext);

  function handleOpenSessionsModal() {
    capture({
      objectId: selectedModule?.isVirtualExerciseModule ? module!.id : selectedModule!.id,
      eventType: ANALYTICS_EVENT_TYPES.RESULTS_ACCESSED,
      subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_TEACHER_ASSIGNMENTS,
    });

    showModal(
      <HistoryModal
        id={modalId}
        scorableItem={scorableItem}
        memberId={memberId}
        groupId={groupId}
        moduleId={selectedModule?.isVirtualExerciseModule ? module!.id : selectedModule!.id}
        isAssignment={isAssignment}
      />,
    );
  }

  async function handleShareModal() {
    try {
      const userMaterialResponse =
        scorableItem.type === 'user-material'
          ? await getUserMaterial(selectedModule!.id, { ids: scorableItem.id, includeNodeHierarchy: true, limit: 1 })
          : { data: [], total: 0 };

      const medialink =
        scorableItem.type === 'medialink' ? await getMedialinkById(selectedModule!.id, scorableItem.id) : undefined;

      showModal(
        <ShareModal
          id="share-modal"
          moduleId={selectedModule!.id}
          nodeId={scorableItem.node}
          userMaterials={userMaterialResponse.data}
          medialinks={medialink ? [medialink] : []}
        />,
      );
    } catch (error) {
      console.error('Error fetching data for ShareModal:', error);
    }
  }

  return (
    <Cell
      buttonGroupProps={{
        'aria-label': t('PAGES.RESULTS.PER_STUDENT.TABLE.SCORABLE_ITEM.SINGLE_STUDENT.ACTIONS.GROUP_A11Y_LABEL'),
        role: 'group',
        className: 'actions button-group',
      }}
      sticky={horizontalScroll}
    >
      <Cell.Button
        startIcon={<RetryIcon data-testid="share-icon" />}
        onClick={handleShareModal}
        prominence="neutral"
        aria-label={t('PAGES.RESULTS.PER_STUDENT.TABLE.SCORABLE_ITEM.SINGLE_STUDENT.ACTIONS.SHARE_BUTTON_A11Y_LABEL')}
      />
      <Cell.Button
        startIcon={<DetailsIcon data-testid="open-sessions-modal" />}
        onClick={handleOpenSessionsModal}
        prominence="neutral"
        aria-label={t(
          'PAGES.RESULTS.PER_STUDENT.TABLE.SCORABLE_ITEM.SINGLE_STUDENT.ACTIONS.SESSION_MODAL_BUTTON_A11Y_LABEL',
        )}
      >
        ({amountOfSessions})
      </Cell.Button>
    </Cell>
  );
}
